import React from 'react';
import { motion } from 'framer-motion';
import { Star, MapPinLine, PencilSimple } from '@phosphor-icons/react';

const VenueCard = ({ venue, onViewDetails, onBook, onEdit, editMode, bookDisabledReason, currencySymbol = '£' }) => {
  if (!venue) return null;
  const price = venue.price_hint ? (venue.price_hint / 100).toFixed(0) : null;

  return (
    <motion.div
      className="ls-venue-card group relative overflow-hidden rounded-xl cursor-pointer"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onViewDetails?.(venue)}
      data-testid={`ls-venue-card-${venue.venue_id}`}
    >
      {/* Full-bleed image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
        style={{
          backgroundImage: venue.photo
            ? `url(${venue.photo})`
            : 'linear-gradient(135deg, var(--ls-elevated) 0%, var(--ls-surface) 100%)',
        }}
      />

      {/* Gradient overlay — darker at bottom for text readability */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.05) 100%)',
        }}
      />

      {/* Top badges */}
      <div className="absolute top-2 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 flex items-start justify-between z-10">
        <span
          className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full backdrop-blur-sm"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          {(venue.subcategory || venue.category || '').replace(/_/g, ' ')}
        </span>
        <div className="flex items-center gap-1.5">
          {venue.status && venue.status !== 'open' && (
            <span className={`text-[9px] sm:text-[10px] font-bold uppercase px-2 py-0.5 sm:py-1 rounded-full ls-status-${venue.status}`}>
              {venue.status}
            </span>
          )}
          {editMode && (
            <button
              className="p-1 sm:p-1.5 rounded-full backdrop-blur-sm transition-colors hover:bg-white/30"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
              onClick={(e) => { e.stopPropagation(); onEdit?.(venue); }}
              title="Edit venue"
            >
              <PencilSimple size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Bottom content overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 z-10">
        {/* Stars */}
        {!!venue.stars && (
          <div className="flex items-center gap-0.5 mb-1 sm:mb-1.5">
            {Array.from({ length: venue.stars }).map((_, i) => (
              <Star key={i} size={11} weight="fill" style={{ color: '#FFB800' }} />
            ))}
          </div>
        )}

        {/* Name */}
        <h3
          className="font-bold text-base sm:text-lg leading-tight mb-0.5 sm:mb-1 line-clamp-2"
          style={{ color: '#fff', fontFamily: 'var(--ls-font-heading)', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
        >
          {venue.name}
        </h3>

        {/* Location */}
        {(venue.address || venue.city) && (
          <p className="text-[10px] sm:text-xs flex items-center gap-1 mb-2 sm:mb-2.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
            <MapPinLine size={11} weight="fill" />
            <span className="truncate">{venue.city || venue.address}</span>
          </p>
        )}

        {/* Price + Book row */}
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            {price && (
              <div className="flex items-baseline gap-0.5 sm:gap-1">
                <span className="text-[10px] sm:text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>From</span>
                <span
                  className="ls-mono text-xl sm:text-2xl font-bold"
                  style={{ color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                >
                  {currencySymbol}{price}
                </span>
                <span className="text-[9px] sm:text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>/ night</span>
              </div>
            )}
          </div>
          <button
            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-[11px] sm:text-xs font-bold transition-all flex-shrink-0"
            style={{
              background: 'var(--ls-primary)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(230,85,0,0.4)',
            }}
            onClick={(e) => { e.stopPropagation(); onBook?.(venue); }}
            disabled={!onBook || !!bookDisabledReason}
            title={bookDisabledReason || 'Book this stay'}
          >
            Book Now
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default VenueCard;
